import { useAuth0 } from '@auth0/auth0-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { COMPANY, isPlaceholder } from './company';

/**
 * The frame around the pages anybody can open WITHOUT signing in: the landing
 * page, the privacy policy and the terms.
 *
 * <p>
 * They have to be public. Google's and Microsoft's app reviewers, and a
 * customer's IT admin deciding whether to approve the app, open these links
 * cold — a page that bounces them to a login is a page they cannot read, and a
 * verification that fails.
 * </p>
 */
export function PublicLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col bg-white">
      <PublicHeader />
      <main className="flex-1">{children}</main>
      <PublicFooter />
    </div>
  );
}

function PublicHeader() {
  return (
    <header className="sticky top-0 z-20 border-b border-white/10 bg-slate-950/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2.5 text-white">
          {/* The favicon IS the Stratum mark; its white dots need a dark ground. */}
          <img src="/favicon.svg" alt="" className="h-8 w-8" />
          <span className="text-base font-semibold tracking-tight">{COMPANY.product}</span>
        </Link>
        <SignInButton variant="header" />
      </div>
    </header>
  );
}

/**
 * Signed out: starts the Auth0 login and comes back to the portal. Signed in
 * (somebody reading the terms from inside the app): goes straight back to it.
 */
export function SignInButton({ variant }: { variant: 'header' | 'hero' }) {
  const { isAuthenticated, loginWithRedirect } = useAuth0();

  const className =
    variant === 'header'
      ? 'rounded-md bg-white/10 px-3.5 py-1.5 text-sm font-medium text-white ring-1 ring-white/20 transition hover:bg-white/20'
      : 'rounded-md bg-[#009ddf] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[#009ddf]/20 transition hover:bg-[#0089c3]';

  if (isAuthenticated) {
    return (
      <Link to="/" className={className}>
        Open {COMPANY.product}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={() => void loginWithRedirect({ appState: { returnTo: '/' } })}
    >
      Sign in
    </button>
  );
}

function PublicFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-slate-200 bg-slate-50">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-8 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div>
          <p>
            © {year} {COMPANY.name}. {COMPANY.product} is a product of {COMPANY.name}.
          </p>
          <p className="mt-1 text-xs text-slate-400">
            {COMPANY.legalName} trading as {COMPANY.name} · CRO {COMPANY.companyNumber} · VAT{' '}
            {COMPANY.vatNumber}
          </p>
        </div>
        <nav className="flex flex-wrap gap-x-6 gap-y-2">
          <Link to="/privacy" className="hover:text-slate-900">
            Privacy policy
          </Link>
          <Link to="/terms" className="hover:text-slate-900">
            Terms of service
          </Link>
          <a href={COMPANY.website} className="hover:text-slate-900">
            stratum.ie
          </a>
          {!isPlaceholder(COMPANY.supportEmail) && (
            <a href={`mailto:${COMPANY.supportEmail}`} className="hover:text-slate-900">
              Support
            </a>
          )}
        </nav>
      </div>
    </footer>
  );
}
